<script setup>
import { getDirectionBackground, randomColor } from '../helpers';

defineProps({
    row: {
        type: Object,
        required: true
    }
});
</script>

<template>
    <div
        class="rounded-lg shadow-sm p-4 flex flex-col gap-2 hover:shadow-lg transition-shadow duration-300 bg-dashboard-primary-light"
        :style="{
            borderLeft: row.direction === 'Long' ? '4px solid #45bf9b' : row.direction === 'Short' ? '4px solid #e84646' : 'none'
        }"
    >
        <div class="flex items-center justify-between gap-2">
            <div class="relative flex-shrink-0">
                <div v-if="row.logo || row.market_symbol_logo" class="w-10 h-10">
                    <img
                        class="w-10 h-10 rounded-xl object-contain"
                        :src="row.logo || row.market_symbol_logo"
                        :alt="row.market || row.market_symbol_name"
                        :class="[row.exit !== '-' ? 'opacity-50' : 'opacity-100']"
                    />
                </div>
                <div v-else :style="{ backgroundColor: randomColor() }" class="w-10 h-10 rounded-xl flex items-center justify-center">
                    <i class="fi fi-rr-apartment text-white text-base"></i>
                </div>

                <!-- Lock-Icon als Overlay -->
                <div
                    v-if="row.exit !== '-'"
                    class="absolute bottom-0 right-0 bg-gray-600/80 rounded-full w-5 h-5 flex items-center justify-center border-2 border-white"
                    :title="$t('trade_closed')"
                >
                    <i class="fi fi-rr-lock text-white text-[8px]"></i>
                </div>
            </div>

            <div class="flex-1">
                <span class="text-xs font-bold">{{ row.market || row.market_symbol_name }}</span>
            </div>

            <div :class="getDirectionBackground(row?.direction)" class="uppercase px-3 rounded-lg text-xxs font-semibold">
                {{ row?.direction }}
            </div>
        </div>

        <div class="grid grid-cols-3 2xl:grid-cols-2 gap-2">
            <div>
                <span class="text-sm text-gray-600">{{ $t('plain_entry') }}</span>
                <p class="text-base font-semibold" :class="row.exit === '-' ? 'text-blue text-base' : ''">{{ row.currency }} {{ row.entry }}</p>
                <p v-if="row.entry_date" class="text-dashboard-primary-grey">{{ row.entry_date }}, {{ row.entry_time }}</p>
            </div>
            <div :class="{ hidden: row.exit === '-' }">
                <span class="text-sm text-gray-600">{{ $t('plain_exit') }}</span>
                <p class="font-semibold text-base" :class="row.exit !== '-' ? 'text-blue text-base' : ''">{{ row.currency }} {{ row.exit }}</p>
                <p v-if="row.exit_date" class="text-dashboard-primary-grey text-xxs">{{ row.exit_date }}, {{ row.exit_time }}</p>
            </div>
            <div v-if="row.exit !== '-'">
                <span class="text-sm text-gray-600">{{ $t('price_gain') }}</span>
                <p :class="[parseFloat(row.performance.replace(',', '.')) < 0 ? 'text-dashboard-lightRed' : 'text-dashboard-lightGreen', 'font-bold text-base']">
                    {{ row.performance }}
                </p>
            </div>
            <div>
                <span class="text-sm text-gray-600">{{ $t('plain_price') }}</span>
                <p class="font-semibold text-base">
                    {{ row.currency }} {{ row.current }}
                    <span
                        v-if="row.exit === '-' && row.performance"
                        :class="[parseFloat(row.performance.replace(',', '.')) < 0 ? 'text-dashboard-lightRed' : 'text-dashboard-lightGreen', 'font-semibold text-sm']"
                    >
                        {{ row.performance }}
                    </span>
                </p>
            </div>
            <div v-if="row.stop !== '-'">
                <span class="text-sm text-gray-600">{{ $t('plain_stop') }}</span>
                <p class="font-semibold text-base">{{ row.stop }}</p>
            </div>
        </div>
        <router-link v-if="row.link && !row.link.endsWith('/null')" :to="row.link">
            <div class="w-full px-2 py-1 rounded-lg font-semibold transition-colors duration-200 border hover:text-blue bg-blue-light text-blue/70">
                <span class="flex items-center gap-1 justify-center">
                    {{ $t('plain_analysis') }}
                    <i class="fi fi-rs-arrow-right text-xs mt-1"></i>
                </span>
            </div>
        </router-link>
        <span v-else class="w-full px-2 py-1 rounded-lg font-semibold border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed" aria-disabled="true">
            <span class="flex items-center gap-1 justify-center">
                {{ $t('plain_analysis') }}
                <i class="fi fi-rs-arrow-right text-xs mt-1"></i>
            </span>
        </span>
    </div>
</template>
